'use client';

/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/react';
import Button from '@egalite/ui/Buttons';
import ErrorList from '@egalite/ui/ErrorList';
import { InputField } from '@egalite/ui/Form';
import SelectField from '@egalite/ui/Form/SelectField';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Loading from '@egalite/ui/Loading';
import Typography from '@egalite/ui/Typography';
import Cookies from 'js-cookie';
import { get } from 'lodash';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import authFetch from '../../lib/auth/authFetch';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/personalData2';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import FormPage from './legacy_blocks/CandidateFormPage';
import save from './save';

const PersonalData2Form: FC<{ edit?: boolean; onSuccess?: () => void }> = ({ edit = false, onSuccess = null }) => {
	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState(initialValues);
	const [fieldsEnabled, setFieldsEnabled] = useState(true);
	const [citiesLoading, setCitiesLoading] = useState(false);
	const [cities, setCities] = useState([]);
	const citySelect = useRef(null);
	const stateSelect = useRef(null);

	const router = useRouter();

	const [formController, getFieldProps, { loading, submitting, success, errors, data }] = useForm({
		initialValues: candidateData,
		validationSchema,
		mutation: data => save('/api/v1/candidate/personalData2', data),
		onSuccess:
			onSuccess != null
				? () => onSuccess()
				: () => {
					router.push(`/candidato/form/professionalData1`, { scroll: true });
				},
	});
	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					if (data.success === true) {
						const newValues = Object.keys(initialValues).reduce((acc, key) => {
							acc[key] = (data.data.address || {})[key] ?? initialValues[key];
							return acc;
						}, {});
						setCandidateData(newValues as typeof initialValues);
						setLoadingData(false);
					} else {
						notification.error('Erro ao carregar dados do candidato');
					}
				});
		}
	}, [formController, router, loadingData, notification]);

	useEffect(() => {
		if (get(candidateData, 'state', false) && stateSelect.current.setSelectedValue) {
			stateSelect.current.setSelectedValue(get(candidateData, 'state', ''));
		}

		if (get(candidateData, 'city', false) && citySelect.current.setSelectedValue) {
			citySelect.current.setSelectedValue(get(candidateData, 'city', ''));
		}
	}, [candidateData, stateSelect, citySelect]);

	useEffect(() => {
		if (formController.values.state) {
			setCitiesLoading(true);
			fetch(`/api/v2/utils/${formController.values.state}/cities`)
				.then(res => res.json())
				.then(data => {
					if (Array.isArray(data)) {
						setCities(data.map(val => val.city));
					} else {
						setCities([]);
					}

					setCitiesLoading(false);
				})
				.catch(err => {
					console.error('Error fetching cities: ', err);
					setCities([]);
					setCitiesLoading(false);
				});

			citySelect.current?.setSelectedValue('');
		}
	}, [formController.values.state]);

	const fetchAddressFromPostalCode = useCallback(
		postalCode => {
			if (postalCode?.replaceAll(/\D/g, '').length > 7) {
				fetch(`/api/v1/getCEP?cep=${postalCode.replace('-', '')}`)
					.then(res => res.json())
					.then(res => {
						if (res.success && res.data) {
							const newValues = formController?.values;
							Object.entries(newValues).forEach(([key, v]) => {
								if (res.data[key] === ' ') return;

								newValues[key] = res.data[key] ?? v;
							});
							newValues['postalCode'] = postalCode;
							formController.setValues(newValues);

							if (res.data.state) {
								stateSelect.current?.setSelectedValue(res.data.state);
							}

							if (res.data.city) {
								citySelect.current?.setSelectedValue(res.data.city);
							}

							if (!res.data.state || !res.data.city) {
								setFieldsEnabled(true);
							} else {
								setFieldsEnabled(false);
							}

							return;
						}

						setFieldsEnabled(true);
					})
					.catch(err => {
						setFieldsEnabled(true);
						console.error('Error fetching address from postal code: ', err);
					});
			}
		},
		[formController, stateSelect],
	);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={2}
			formActualStep={2}
			formTotalSteps={2}
			progress={100}
			formTitle={'Dados pessoais'}
			// previousForm="/"
			showStepsMenu={false}
			modal={edit}
		// showBackButton={false}
		>
			<form onSubmit={formController.handleSubmit}>
				<div className="mb-16 md:mb-24">
					<Typography variant="headline-5" className="text-brand-main mb-16">
						Endereço
					</Typography>
					<InputField
						id="postalCode"
						type="text"
						label={'CEP'}
						maskOptions={{ mask: '00000-000', lazy: false, placeholderChar: '_' }}
						{...{
							...getFieldProps('postalCode'),
							setValue: value => {
								getFieldProps('postalCode').setValue(value);
								fetchAddressFromPostalCode(value);
							},
						}}
					/>
					<SelectField
						className="my-12"
						ref={stateSelect}
						id="state"
						placeholder="Selecione..."
						label="Estado"
						disabled={!fieldsEnabled}
						options={[
							{ value: 'AC', label: 'Acre' },
							{ value: 'AL', label: 'Alagoas' },
							{ value: 'AP', label: 'Amapá' },
							{ value: 'AM', label: 'Amazonas' },
							{ value: 'BA', label: 'Bahia' },
							{ value: 'CE', label: 'Ceará' },
							{ value: 'DF', label: 'Distrito Federal' },
							{ value: 'ES', label: 'Espírito Santo' },
							{ value: 'GO', label: 'Goiás' },
							{ value: 'MA', label: 'Maranhão' },
							{ value: 'MT', label: 'Mato Grosso' },
							{ value: 'MS', label: 'Mato Grosso do Sul' },
							{ value: 'MG', label: 'Minas Gerais' },
							{ value: 'PA', label: 'Pará' },
							{ value: 'PB', label: 'Paraíba' },
							{ value: 'PR', label: 'Paraná' },
							{ value: 'PE', label: 'Pernambuco' },
							{ value: 'PI', label: 'Piauí' },
							{ value: 'RJ', label: 'Rio de Janeiro' },
							{ value: 'RN', label: 'Rio Grande do Norte' },
							{ value: 'RS', label: 'Rio Grande do Sul' },
							{ value: 'RO', label: 'Rondônia' },
							{ value: 'RR', label: 'Roraima' },
							{ value: 'SC', label: 'Santa Catarina' },
							{ value: 'SP', label: 'São Paulo' },
							{ value: 'SE', label: 'Sergipe' },
							{ value: 'TO', label: 'Tocantins' },
						]}
						{...getFieldProps('state')}
					/>
					<SelectField
						className="my-12"
						id="city"
						ref={citySelect}
						label="Cidade"
						options={cities.map(city => ({ value: city, label: city }))}
						disabled={!fieldsEnabled || citiesLoading || cities.length === 0}
						{...getFieldProps('city')}
					/>
					<InputField id="place" type="text" label={'Nome da rua'} {...getFieldProps('place')} />
					<InputField id="number" type="text" label={'Número'} {...getFieldProps('number')} />
					<InputField
						id="complement"
						type="text"
						label={'Complemento'}
						required={false}
						{...getFieldProps('complement')}
					/>
		
					<InputField
						id="country"
						type="hidden"
						value="BRA"
						{...getFieldProps('country')}
					/>

					<ErrorList errors={formController.errors} touched={formController.touched} />

					{errors != null &&
						errors.map((err, index) => (
							<Typography key={`err-${index}`} variant="body2" color="error.dark">
								{err}
							</Typography>
						))}
				</div>
				<div className="md:mt-24 mt-16">
					{edit ? (
						<Button type="submit" variant="primary" className="w-full">
							Salvar
						</Button>
					) : (
						<ButtonsPages previousForm="/candidato/form/personalData1" />
					)}
				</div>
			</form>
		</FormPage>
	);
};

export default PersonalData2Form;
